import { Injectable } from '@angular/core';

export interface CameraDevice {
  deviceId: string;
  label: string;
  kind: string;
}

export interface CameraCapabilities {
  hasCamera: boolean;
  cameras: CameraDevice[];
  facingMode: 'user' | 'environment' | null;
}

@Injectable({
  providedIn: 'root',
})
export class CameraService {
  private stream: MediaStream | null = null;
  
  // TEMPORARY: Flag to force camera in use error for testing
  // private FORCE_CAMERA_IN_USE_ERROR = true;

  constructor() {}

  /**
   * Verifica se o dispositivo tem câmeras disponíveis
   */
  async checkCameraAvailability(): Promise<CameraCapabilities> {
    try {
      console.log('CameraService: Checking camera availability...');
      
      // Verifica se a API está disponível
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        console.log('CameraService: MediaDevices API not available');
        return {
          hasCamera: false,
          cameras: [],
          facingMode: null,
        };
      }

      // Enumera os dispositivos
      const devices = await navigator.mediaDevices.enumerateDevices();
      console.log('CameraService: All devices:', devices);
      
      const videoDevices = devices.filter(
        (device) => device.kind === 'videoinput'
      );

      console.log('CameraService: Video devices found:', videoDevices);

      const cameras: CameraDevice[] = videoDevices.map((device, index) => ({
        deviceId: device.deviceId,
        label: device.label || `Camera ${index + 1}`,
        kind: device.kind,
      }));

      // Detecta se tem câmera frontal ou traseira
      let facingMode: 'user' | 'environment' | null = null;
      if (cameras.length > 0) {
        // Em dispositivos móveis, tenta usar a câmera traseira por padrão
        const backCamera = cameras.find((cam) =>
          cam.label.toLowerCase().includes('back')
        );
        facingMode = backCamera ? 'environment' : 'user';
      }

      console.log('CameraService: Camera capabilities:', {
        hasCamera: cameras.length > 0,
        count: cameras.length,
        facingMode,
      });

      return {
        hasCamera: cameras.length > 0,
        cameras,
        facingMode,
      };
    } catch (error) {
      console.error('CameraService: Error checking camera availability:', error);
      return {
        hasCamera: false,
        cameras: [],
        facingMode: null,
      };
    }
  }

  /**
   * Inicia o stream da câmera
   */
  async startCamera(
    facingMode: 'user' | 'environment' = 'environment',
    deviceId?: string
  ): Promise<MediaStream> {
    try {
      // TEMPORARY: Force camera in use error for testing
      // if (this.FORCE_CAMERA_IN_USE_ERROR) {
      //   const error = new Error('Camera is already in use by another application.');
      //   (error as any).name = 'NotReadableError';
      //   throw error;
      // }

      // Para qualquer stream anterior
      this.stopCamera();

      console.log('CameraService: Starting camera with facingMode:', facingMode);

      const constraints: MediaStreamConstraints = {
        video: deviceId
          ? { deviceId: { exact: deviceId } }
          : {
              facingMode: facingMode,
              width: { ideal: 1920 },
              height: { ideal: 1080 },
            },
        audio: false,
      };

      console.log('CameraService: Constraints:', constraints);

      this.stream = await navigator.mediaDevices.getUserMedia(constraints);
      
      console.log('CameraService: Stream obtained successfully:', this.stream);
      console.log('CameraService: Active tracks:', this.stream.getVideoTracks());
      
      return this.stream;
    } catch (error) {
      console.error('CameraService: Error starting camera:', error);
      throw this.handleCameraError(error);
    }
  }

  /**
   * Para o stream da câmera
   */
  stopCamera(): void {
    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
      this.stream = null;
    }
  }

  /**
   * Captura uma foto do stream da câmera
   */
  capturePhoto(
    videoElement: HTMLVideoElement,
    quality: number = 0.95
  ): Promise<Blob> {
    return new Promise((resolve, reject) => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = videoElement.videoWidth;
        canvas.height = videoElement.videoHeight;

        const context = canvas.getContext('2d');
        if (!context) {
          reject(new Error('Could not get canvas context'));
          return;
        }

        // Desenha o frame atual do vídeo no canvas
        context.drawImage(videoElement, 0, 0, canvas.width, canvas.height);

        // Converte para blob
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Failed to capture image'));
            }
          },
          'image/jpeg',
          quality
        );
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Troca entre câmera frontal e traseira
   */
  async switchCamera(currentFacingMode: 'user' | 'environment'): Promise<MediaStream> {
    const newFacingMode = currentFacingMode === 'user' ? 'environment' : 'user';
    return this.startCamera(newFacingMode);
  }

  /**
   * Trata erros da câmera
   */
  private handleCameraError(error: any): Error {
    if (error.name === 'NotAllowedError') {
      return new Error('CameraPermissionDenied');
    } else if (error.name === 'NotFoundError') {
      return new Error('NoCameraFound');
    } else if (error.name === 'NotReadableError') {
      return new Error('CameraAlreadyInUse');
    } else if (error.name === 'OverconstrainedError') {
      return new Error('CameraConstraintsNotSupported');
    } else {
      return new Error(error.message || 'CameraErrorUnknown');
    }
  }

  /**
   * Verifica se o navegador suporta getUserMedia
   */
  isSupported(): boolean {
    return !!(
      navigator.mediaDevices && navigator.mediaDevices.getUserMedia
    );
  }
}
